import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

export const fetchIndentData = createAsyncThunk(
  'indent/fetchData',
  async ({ role, enteredBy }, { rejectWithValue }) => {
    try {
      const endpoint = role === "admin" 
        ? `${API_BASE_URL}/adminpage/indent`
        : `${API_BASE_URL}/indent/indent`;

      const response = await axios.get(endpoint, {
        params: {
          ...(role !== "admin" && { ProjectIncharge: enteredBy }),
        },
      });

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchDropdownOptions = createAsyncThunk(
  'indent/fetchDropdownOptions',
  async (_, { rejectWithValue }) => {
    try {
      const [usersRes, projectsRes, stagesRes, typesRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/dropdown/users`),
        axios.get(`${API_BASE_URL}/dropdown/mprojects`),
        axios.get(`${API_BASE_URL}/dropdown/mstages`),
        axios.get(`${API_BASE_URL}/dropdown/mindenttypes`),
      ]);

      return {
        users: usersRes.data.map(user => ({ id: user.UserID, name: user.Username })),
        projects: projectsRes.data.map(project => ({ id: project.ProjectID, name: project.ProjectName })),
        stages: stagesRes.data.map(stage => ({ id: stage.StageID, name: stage.StageName })),
        types: typesRes.data.map(type => ({ id: type.TypeID, name: type.TypeName })),
      };
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

const indentSlice = createSlice({
  name: 'indent',
  initialState: {
    indents: [],
    filteredIndents: [],
    dropdownData: {
      users: [],
      projects: [],
      stages: [],
      types: [],
    },
    selectedRow: null,
    isLoading: false,
    error: null,
  },
  reducers: {
    setSelectedRow: (state, action) => {
      state.selectedRow = action.payload;
    },
    filterIndents: (state, action) => {
      const query = action.payload.toLowerCase();
      state.filteredIndents = query
        ? state.indents.filter(indent => 
            indent.IndentID.toString().toLowerCase().includes(query) ||
            (indent.Status?.toLowerCase().includes(query)) ||
            (indent.ProjectName?.toLowerCase().includes(query)) ||
            (indent.TypeName?.toLowerCase().includes(query)) ||
            (indent.CreationDate?.toString().toLowerCase().includes(query))
          )
        : state.indents;
    },
    filterByDate: (state, action) => {
      const { dateFrom, dateTo } = action.payload;
      if (dateFrom && dateTo) {
        state.filteredIndents = state.indents
          .filter(indent => {
            const creationDate = new Date(indent.CreationDate);
            return creationDate >= new Date(dateFrom) && creationDate <= new Date(dateTo);
          })
          .sort((a, b) => new Date(b.CreationDate) - new Date(a.CreationDate));
      } else {
        state.filteredIndents = state.indents;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchIndentData.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchIndentData.fulfilled, (state, action) => {
        const formattedIndents = action.payload
          .map(indent => ({
            ...indent,
            L1: indent.L1 ? "Approved" : "Pending",
            L2: indent.L2 ? "Approved" : "Pending",
            PO: indent.PO ? "Approved" : "Pending",
            PH: indent.PH ? "Approved" : "Pending",
            IR: indent.IR ? "Approved" : "Pending",
            CreationDate: new Date(indent.CreationDate)
              .toLocaleDateString("en-GB")
              .replace(/\//g, "-"),
          }))
          .sort((a, b) => new Date(b.CreationDate) - new Date(a.CreationDate));
        
        state.indents = formattedIndents;
        state.filteredIndents = formattedIndents;
        state.isLoading = false;
        state.error = null;
      })
      .addCase(fetchIndentData.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchDropdownOptions.fulfilled, (state, action) => {
        state.dropdownData = action.payload;
      });
  },
});

export const { setSelectedRow, filterIndents, filterByDate } = indentSlice.actions;
export default indentSlice.reducer;